'use client';

import { useCallback } from 'react';
import {
  APPLICATION_DISCOVERY_STATUS_CHANGED_SSE_EVENT,
  type ApplicationDiscoveryStatus,
} from '@terra/shared-contracts-applications';
import { useDiscoveryStatus } from '@terra/ui-design-system';
import { useSSE } from '../app/providers/SseProvider';
import { useInvalidateDiscoveryAssets } from './useAssetsDiscoveryRefresh';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';

async function fetchDiscoveryStatus(
  applicationId: string,
): Promise<ApplicationDiscoveryStatus> {
  const authHeaders = await getAuthHeaders();
  const response = await applicationsClient.getDiscoveryStatus({
    params: { id: applicationId },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
    },
    extraHeaders: {
      ...authHeaders,
    },
  });

  if (response.status === 200) {
    return response.body.status;
  }

  throw new Error(
    `Discovery status fetch failed with status ${response.status}`,
  );
}

export function useApplicationDiscoveryStatus({
  applicationId,
  enabled,
}: {
  applicationId: string | undefined;
  enabled?: boolean;
}) {
  const { subscribe, subscribeReconnect } = useSSE();
  const invalidateDiscoveryAssets = useInvalidateDiscoveryAssets();

  const invalidateAssets = useCallback(() => {
    if (applicationId) {
      void invalidateDiscoveryAssets({ applicationId });
    }
  }, [applicationId, invalidateDiscoveryAssets]);

  const { isDiscoveryActive, isProcessingActive, isDeduplicatingActive } =
    useDiscoveryStatus({
      applicationId,
      subscribe,
      subscribeReconnect,
      fetchStatus: fetchDiscoveryStatus,
      invalidateAssets,
      enabled,
      sseEventDef: APPLICATION_DISCOVERY_STATUS_CHANGED_SSE_EVENT,
    });

  return {
    isDiscoveryActive,
    isParametrizationActive: isProcessingActive,
    isDeduplicationActive: isDeduplicatingActive,
  };
}
